/* Model checks — built to the Claude Design reference's "Model checks" screen (`Lumiq App.dc.html`).
 *
 * `core.ai.evals` answers one question from stored evidence: which model has been shown capable of
 * which AI feature here. The answer is a GATE rather than a report — it is consulted before a
 * feature runs — and four things this screen is deliberate about.
 *
 * **The reference is a flat list of (model, capability) pairs, not a matrix.** This screen used to
 * be a grid with capabilities as columns, which is denser and answers the wrong question: a reader
 * arrives holding one model and one feature, and the useful thing beside a verdict is the evidence
 * for it. The reference gives every pair a row and an *Evidence* column carrying the arithmetic in
 * prose, which is what makes this evidence rather than a declaration.
 *
 * **The asymmetry is shown, not buried.** Absence of evidence permits and evidence of failure
 * blocks: an untested model is allowed, because failing closed would have disabled every AI feature
 * on every deployment the day the gate shipped. Only `failed` withholds — the reference states it in
 * the lede and again in the footnote, and both are rendered here.
 *
 * **The counts sit beside the verdict, because the verdict is derived from them and never stored.**
 * Raising a bar re-derives every historical verdict from evidence that never moved, which is only
 * defensible if a reader can see the counts and disagree.
 *
 * **`meets_specification` and the server's own two policy sentences are rendered, not paraphrased.**
 * A04/A16 asks for ≥300 golden and ≥200 NL→rule cases and this deployment ships a fraction; a screen
 * restating either the shortfall or the gating rule in its own words is where the claim drifts from
 * the mechanism. The reference shows neither the corpus card nor the run panel — it is a static
 * mockup of the table — and both are kept, because removing them would remove this feature's only
 * execution path and its only statement of what a verdict is worth.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { HelpLabel } from '@/shared/ui/InfoTip';

import { useCapabilityMatrix, useRunEvaluation, useSuiteReport } from '../api/evals';
import type { Capability, CapabilityVerdict } from '../api/evals';
import {
  capabilityKey,
  capabilityRows,
  corpusShortfall,
  evidenceFor,
  gatedCount,
  unavailableOf,
  verdictFor,
  verdictKey,
  verdictTone,
} from '../model/presentation';

/** The reference's verdict chip: a 6px square dot then the label.
 *
 * Filled for a verdict that came from a measurement and an outline for one that did not, so
 * "passed" and "unproven" differ in *shape* as well as hue — the distinction survives greyscale,
 * which matters because three of the four verdicts permit and only one blocks.
 */
function VerdictChip({ verdict }: { readonly verdict: CapabilityVerdict }): ReactElement {
  const { t } = useTranslation();
  const tone = verdictTone(verdict);
  /* `--danger` is this kit's badge tone name and `--error` the reference's chip modifier; mapped
     here rather than renaming either, so the shared chip classes stay the reference's. */
  const modifier = tone === 'danger' ? 'error' : tone === 'ok' ? 'ok' : 'unknown';
  const measured = verdict === 'proven' || verdict === 'failed';
  return (
    <span
      className={`lq-ai__chip lq-ai__chip--${modifier}`}
      title={t(`${verdictKey(verdict)}.meaning`)}
    >
      <span className={`lq-ai__dot${measured ? ' lq-ai__dot--filled' : ''}`} />
      {t(verdictKey(verdict))}
    </span>
  );
}

function Corpus(): ReactElement | null {
  const { t } = useTranslation();
  const suite = useSuiteReport();
  if (!suite.isSuccess) return null;
  const { suite_version, capabilities, meets_specification, note } = suite.data;
  const shortfall = corpusShortfall(capabilities);

  return (
    <Card
      title={t('evals.corpus.title')}
      help={t('help.term.testCorpus')}
      hint={t('evals.corpus.hint', { version: suite_version })}
    >
      <div className="lq-stack">
        {/* A warning when the corpus falls short, because that is what bounds how much any verdict on
            this screen is worth. The server's own `note` carries the reasoning; repeating it here in
            different words is where the two would drift. */}
        {!meets_specification && (
          <Alert tone="warning" title={t('evals.corpus.shortTitle', shortfall)}>
            {note}
          </Alert>
        )}
        <DataTable
          caption={t('evals.corpus.tableCaption')}
          columns={[
            { key: 'capability', label: t('evals.corpus.column.capability') },
            { key: 'here', label: t('evals.corpus.column.here'), numeric: true },
            {
              key: 'specified',
              label: t('evals.corpus.column.specified'),
              numeric: true,
            },
            { key: 'minimum', label: t('evals.corpus.column.minimum'), numeric: true },
            { key: 'bar', label: t('evals.corpus.column.bar'), numeric: true },
          ]}
        >
          {capabilities.map((capability) => (
            <tr key={capability.code}>
              <td>{t(capabilityKey(capability.code))}</td>
              {/* Warn-toned where this capability falls short of the specification, so the row
                  that bounds a verdict's worth is the one a reader's eye lands on — the banner above
                  says how short the corpus is, this says where. */}
              <td
                className={
                  capability.cases_here < capability.cases_specified
                    ? 'lq-table__num lumiq-numeric lq-ai__short'
                    : 'lq-table__num lumiq-numeric'
                }
              >
                {capability.cases_here}
              </td>
              <td className="lq-table__num lumiq-numeric">
                {capability.cases_specified}
              </td>
              {/* The floor below which a run concludes `insufficient_evidence` rather than a verdict.
                  Shown because "4 of a required 10" is actionable and the verdict alone is not. */}
              <td className="lq-table__num lumiq-numeric">
                {capability.minimum_to_conclude}
              </td>
              <td className="lq-table__num lumiq-numeric">
                {(capability.minimum_pass_rate * 100).toFixed(0)}%
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
    </Card>
  );
}

function RunPanel(): ReactElement {
  const { t } = useTranslation();
  const suite = useSuiteReport();
  const run = useRunEvaluation();
  const capabilities = suite.data?.capabilities ?? [];

  return (
    <Card title={t('evals.run.title')} hint={t('evals.run.hint')}>
      <div className="lq-stack">
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {capabilities.map((capability) => (
            <Button
              key={capability.code}
              /* **`run.isPending` unscoped, and the narrowing this replaced was a billable defect.**
                 `useMutation` is a single-slot observer, so `run.variables === capability.code` left
                 the other three buttons live while a run was in flight — and `idempotencyKeyFor`
                 memoises until `onSuccess` clears it, so a second click during the first run sent
                 **two concurrent POSTs under one key**. `EvalService.run` checks `run_under` before
                 the corpus executes, and under READ COMMITTED neither request sees the other's
                 uncommitted row, so both ran every case: twice the completions, all metered on
                 `Meter.LLM_TOKENS`, with migration 0064's index then discarding one result.

                 The narrowing was borrowed from `SuggestionsScreen`, where it stops a stale error
                 being misattributed to the wrong dataset on a **one-button** panel. On four
                 simultaneously-clickable buttons the same expression has the opposite effect. Both
                 sibling AI screens use the unscoped form; this is the first billable multi-button
                 panel in the app, which is why the difference had not bitten before. */
              busy={run.isPending}
              /* Offered even to a caller who will be refused, deliberately. `ai.evals:run` reaches
                 admin only — a run is real model usage and its output gates features — and hiding the
                 control would make "why can I not re-test this model" unanswerable from the screen
                 where the question arises. The 403 says which permission, which is the useful answer. */
              disabled={capability.cases_here === 0}
              title={capability.cases_here === 0 ? t('evals.run.noCases') : undefined}
              onClick={() => {
                run.mutate(capability.code);
              }}
            >
              {t('evals.run.action', { capability: t(capabilityKey(capability.code)) })}
            </Button>
          ))}
        </div>
        {/* **Both outcomes name the capability**, because there are four buttons and one result
            line. "Tested llama-3: Proven" says nothing about which feature was proven, and a reader
            arriving after somebody else clicked cannot tell — on the panel a person reads immediately
            after paying for the run. `run.variables` is the capability the mutation was called with,
            so the label cannot disagree with the request. */}
        {run.isError && (
          <Alert
            tone="error"
            title={t('evals.run.failed', {
              capability: t(capabilityKey(run.variables)),
            })}
          >
            {run.error.message}
          </Alert>
        )}
        {run.isSuccess && (
          <p className="lq-muted">
            {t('evals.run.done', {
              model: run.data.model,
              capability: t(capabilityKey(run.data.capability)),
              verdict: t(verdictKey(run.data.verdict)),
            })}
          </p>
        )}
      </div>
    </Card>
  );
}

export function CapabilityMatrixScreen(): ReactElement {
  const { t } = useTranslation();
  const matrix = useCapabilityMatrix();
  const suite = useSuiteReport();

  const results = matrix.data?.results ?? [];
  /* **The capability order falls back to the matrix's own, and the reason is a blank table.** It came
     from `/suite` alone, so a failure there — a 429 is the realistic one — left the order empty, the
     row builder produced nothing, and the screen rendered an empty table while nothing said the suite
     had failed. Deriving from `results` when the suite is unavailable also keeps a result whose
     capability the suite does not list: recorded, gating, and otherwise invisible. */
  const capabilityOrder: readonly Capability[] = suite.data?.capabilities.map(
    (entry) => entry.code,
  ) ?? [...new Set(results.map((result) => result.capability))];
  const rows = capabilityRows(results, capabilityOrder);
  const withheld = gatedCount(results);

  return (
    <div className="lq-ai">
      <div className="lq-ai__head">
        <div className="lq-ai__head-main">
          <HelpLabel subject={t('evals.title')} help={t('help.feature.modelChecks')}>
            <h1 className="lq-ai__title">{t('evals.title')}</h1>
          </HelpLabel>
          <p className="lq-ai__lede">{t('evals.lede')}</p>
        </div>
      </div>

      <div className="lq-ai__stack">
        {/* The one urgent thing this screen can say, and it stays above the table: a withheld model
            is a feature a tenant is not getting. The reference's footnote states the rule; this
            states the instance. */}
        {withheld > 0 && (
          <Alert tone="warning" title={t('evals.policy.withheldTitle')}>
            {t('evals.policy.withheldBody', { count: withheld })}
          </Alert>
        )}

        {suite.isError && (
          <Alert tone="error" title={t('evals.corpus.unreadable')}>
            {suite.error.message}
          </Alert>
        )}

        {matrix.isError && (
          <Alert tone="error" title={t('evals.unreadable')}>
            {matrix.error.message}
          </Alert>
        )}

        {matrix.isPending && <SkeletonRows columns={4} rows={4} />}

        {matrix.isSuccess &&
          (rows.length === 0 ? (
            <EmptyState
              guidance={t('evals.emptyGuidance')}
              title={t('evals.emptyTitle')}
            />
          ) : (
            <div>
              {/* The reference's 2px `--domain-ai` rule along the top of the table — the one place
                  AI takes a colour of its own here, and in this palette that colour is a neutral
                  slate rather than a hue. */}
              <div className="lq-ai__table-wrap lq-ai__card--ai">
                <table className="lq-ai__table">
                  <caption className="lq-visually-hidden">
                    {t('evals.tableCaption')}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('evals.column.model')}</th>
                      <th scope="col">{t('evals.column.capability')}</th>
                      <th scope="col">{t('evals.column.verdict')}</th>
                      <th className="lq-ai__num" scope="col">
                        {t('evals.column.evidence')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const verdict = verdictFor(row.result);
                      const bar = matrix.data.bars[row.capability];
                      /* `POSITIVE_INFINITY`, not `0`. A missing bar with `?? 0` made the
                         thin-evidence arithmetic go quiet in the one case where the screen cannot
                         check it — failing toward flagging is §8's reflex applied to a number. */
                      const evidence = evidenceFor(
                        row.result,
                        bar?.minimum_cases ?? Number.POSITIVE_INFINITY,
                        bar?.minimum_pass_rate ?? 1,
                      );
                      return (
                        <tr key={`${row.model}\u0000${row.capability}`}>
                          <td className="lq-ai__mono">{row.model}</td>
                          <td className="lq-ai__muted">
                            {t(capabilityKey(row.capability))}
                          </td>
                          <td>
                            <VerdictChip verdict={verdict} />
                          </td>
                          <td className="lq-ai__num lq-ai__evidence">
                            <span className="lq-ai__figures">
                              {t(evidence.key, evidence.values)}
                            </span>
                            {/* Kept from the previous build because it is real information the
                                reference had no column for: a case the model could not be reached
                                for is excluded from the bar entirely, so a run can be a partial
                                measurement and the verdict alone would not say so. */}
                            {row.result !== null && unavailableOf(row.result) > 0 && (
                              <span className="lq-ai__aside">
                                {t('evals.unreachable', {
                                  count: unavailableOf(row.result),
                                })}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="lq-ai__footnote">{t('evals.footnote')}</p>
            </div>
          ))}

        <RunPanel />
        <Corpus />

        {/* The server's own two sentences. The reference's lede and footnote say the same two things
            more briefly; these stay because they are the *deployment's* statement read from the
            running code, and a screen paraphrasing a security control is where the claim drifts
            from the mechanism. */}
        {matrix.isSuccess && (
          <div className="lq-ai__card">
            <p className="lq-muted">{matrix.data.gating_policy}</p>
            <p className="lq-dim lq-ai__last">
              {matrix.data.a_model_cannot_certify_itself}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
